import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
	AdressverwaltungFacade,
	KontaktAnrede,
	KontaktAnredeLabel,
	KontaktLinkRel,
	KontaktResource,
} from '@schir-int-client/adressverwaltung-shared';
import { debounce, isNil } from 'lodash-es';
import { KontaktFormService } from './kontakt.formservice';
import { AccessibilityService, DialogService, PotentiallyDirty, Selectable } from '@schir-int-client/tech';
import { Subscription } from 'rxjs';
import { VerfahrenFacade, VerfahrenLinkRel, VerfahrenResource } from '@schir-int-client/verfahren-shared';
import { getEmbeddedResource } from '@ngxp/rest';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import { ApiRootFacade } from '@schir-int-client/api-root';
import { DialogDataYesNo } from '@schir-int-client/dialog-shared';
import {
	AktenzeichenRenderer,
	AktenzeichenRendererProvider,
	AktenzeichenUtil,
} from '@schir-int-client/aktenzeichen-shared';

@Component({
	selector: 'schir-int-client-adressverwaltung-kontakt-view',
	templateUrl: './adressverwaltung-kontakt-view.component.html',
	styleUrls: ['./adressverwaltung-kontakt-view.component.scss'],
	standalone: false,
})
export class AdressverwaltungKontaktViewComponent implements PotentiallyDirty, OnDestroy, AfterViewInit {
	@Input() selectedKontakt: KontaktResource;
	@Input() editMode: boolean;

	@Output() onSubmit: EventEmitter<any> = new EventEmitter();

	linkRel = KontaktLinkRel;
	idPrefix: string;
	aehnlicherKontakt: boolean;

	private subscriptions: Subscription[] = [];

	selectedAktenzeichen: string;
	behoerdenAutoKontaktSelectionList: string[];

	private readonly MSG_ACTIVATE = 'Wollen Sie diesen Kontakt wirklich aktivieren?';
	private readonly MSG_INACTIVATE = 'Wollen Sie diesen Kontakt wirklich inaktivieren?';
	private readonly MSG_MULTIPLE = 'Der Kontakt ist folgenden Verfahren zugeordnet. Trotzdem inaktivieren?';
	readonly MSG_DUPLICATE_JUR = 'Es gibt bereits einen juristischen Kontakt mit dem Firmennamen.';
	readonly MSG_DUPLICATE_NAT = 'Es gibt bereits einen Kontakt mit demselben Vornamen und Namen.';

	private aktenzeichenRenderer: AktenzeichenRenderer;

	anredeValues: Selectable[] = [
		{ label: KontaktAnredeLabel.GUTEN_TAG, value: KontaktAnrede.GUTEN_TAG },
		{ label: KontaktAnredeLabel.HERR, value: KontaktAnrede.HERR },
		{ label: KontaktAnredeLabel.FRAU, value: KontaktAnrede.FRAU },
	];

	juristischeAnredeValues: Selectable[] = [
		{ label: KontaktAnredeLabel.GUTEN_TAG, value: KontaktAnrede.GUTEN_TAG },
		{ label: KontaktAnredeLabel.HERR, value: KontaktAnrede.HERR },
		{ label: KontaktAnredeLabel.FRAU, value: KontaktAnrede.FRAU },
		{ label: KontaktAnredeLabel.EMPTY, value: KontaktAnredeLabel.EMPTY },
	];

	readonly debouncedCheckForSimilarContact = debounce(this.checkForSimilarContact, 1000, { leading: true });

	constructor(
		private formService: KontaktFormService,
		private facade: AdressverwaltungFacade,
		private accessibilityService: AccessibilityService,
		private dialogService: DialogService,
		private verfahrenFacade: VerfahrenFacade,
		private apiRootFacade: ApiRootFacade,
		aktenzeichenRendererProvider: AktenzeichenRendererProvider,
		private cdr: ChangeDetectorRef
	) {
		this.idPrefix = 'i' + Math.random().toString(36).slice(2, 9) + '_'; // good enough
		this.subscriptions.push(
			this.verfahrenFacade.verfahrenSingle.subscribe(verfahren => {
				if (!isNil(verfahren)) {
					this.selectedAktenzeichen = verfahren.aktenzeichen;
				}
			})
		);
		this.subscriptions.push(
			this.apiRootFacade
				.getCurrentApiRoot()
				.pipe(filter(apiRoot => !isNil(apiRoot)))
				.subscribe(apiRoot => {
					this.behoerdenAutoKontaktSelectionList = apiRoot.features.behoerdeAutoKontakt;
				})
		);
		// Verhindert esoterischen Fehler bei sehr schnellem Klicken auf andere Kontakte vor Speichern des aktuellen Kontakts:
		this.subscriptions.push(
			this.facade.selectedKontakt.subscribe(k => {
				this.formService.patch(k);
				this.cdr.markForCheck();
			})
		);

		this.aktenzeichenRenderer = aktenzeichenRendererProvider.getRenderer();
	}

	ngAfterViewInit() {
		this.subscriptions.push(
			this.facade.editMode.subscribe(editMode => {
				if (editMode) {
					setTimeout(() => {
						this.accessibilityService.focusElementById('jurCheckbox');
					});
				}
			})
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	get form(): UntypedFormGroup {
		return this.formService.form;
	}

	getFormControl(controlName: string): UntypedFormControl {
		const ctrl = <UntypedFormControl>this.form.controls[controlName];
		ctrl.enable();
		return ctrl;
	}

	isFirmendatenEmpty() {
		const c = this.form.controls;
		return (
			c['firmenName'].value == null &&
			c['firmenName1'].value == null &&
			c['firmenName2'].value == null &&
			c['sitz'].value == null &&
			c['zustaendigesAmtsgericht'].value == null &&
			c['handelsregisterNummer'].value == null
		);
	}

	isPersonendatenEmpty() {
		const c = this.form.controls;
		return (
			c['anrede'].value == null &&
			c['titel'].value == null &&
			c['vorname'].value == null &&
			c['name'].value == null &&
			c['geburtsname'].value == null &&
			c['namenszusatz'].value == null &&
			c['geburtsDatum'].value == null
		);
	}

	isKontaktdatenEmpty() {
		const c = this.form.controls;
		return (
			c['strasse'].value == null &&
			c['hausnummer'].value == null &&
			c['adresszusatz'].value == null &&
			c['plz'].value == null &&
			c['stadt'].value == null &&
			c['land'].value == null &&
			c['postfach'].value == null &&
			c['telefon'].value == null &&
			c['email'].value == null
		);
	}

	isSonstigeDatenEmpty() {
		const c = this.form.controls;
		return (
			c['telefon2'].value == null &&
			c['telefon3'].value == null &&
			c['fax'].value == null &&
			c['egvpAdresse'].value == null &&
			c['deMail'].value == null &&
			c['notiz'].value == null
		);
	}

	private checkForSimilarContact() {
		this.facade
			.existsOtherKontaktByName(
				this.selectedKontakt,
				this.form.controls['juristischePerson'].value,
				this.form.controls['firmenName'].value?.toString() ?? '',
				this.form.controls['vorname'].value?.toString() ?? '',
				this.form.controls['name'].value?.toString() ?? ''
			)
			.pipe(take(1))
			.subscribe(b => (this.aehnlicherKontakt = b));
	}

	edit(): void {
		this.aehnlicherKontakt = false;
		this.formService.activateEditMode();
		this.facade.requestActionOnSelectedKontakt();
	}

	submit(): void {
		if (this.formService.submit()) {
			this.subscriptions.push(
				this.facade.selectedKontakt
					.pipe(
						filter(kontakt => Boolean(kontakt.title)),
						take(1)
					)
					.subscribe(kontakt => {
						this.formService.kontaktResource = kontakt;
						this.openKontaktAssignMenu();
					})
			);
			this.formService.deactivateEditMode();
			this.formService.form.markAsPristine();
		}
	}

	private openKontaktAssignMenu() {
		this.facade.renderedKontakt
			.pipe(
				withLatestFrom(this.facade.selectedKontakt),
				filter(([, selected]) => this.matchesInputForm(selected)),
				take(1)
			)
			.subscribe(([kontaktDiv]) => {
				kontaktDiv.nativeElement.querySelector('button').click();
			});
	}

	reset() {
		// nothing to do
	}

	isValid(): boolean {
		return this.form.valid;
	}

	isDirty(): boolean {
		return this.form.dirty;
	}

	inactivate() {
		this.facade.requestActionOnSelectedKontakt();
		this.subscriptions.push(
			this.facade.getAllVerfahrenForKontakt(this.selectedKontakt).subscribe(vList => {
				const verfahrenList: VerfahrenResource[] = getEmbeddedResource(vList, VerfahrenLinkRel.VERFAHREN_LIST);

				if (verfahrenList) {
					if (this.belongsToOtherVerfahren(verfahrenList)) {
						const additionalContent = [];

						verfahrenList.forEach(verfahren => {
							const aktenzeichen = AktenzeichenUtil.parse(verfahren.aktenzeichen);
							additionalContent.push(this.aktenzeichenRenderer.render(aktenzeichen));
						});

						this.showDialog(additionalContent);
					} else {
						this.showDialog([]);
					}
				} else {
					this.showDialog([]);
				}
			})
		);
	}

	private belongsToOtherVerfahren(verfahrenList: VerfahrenResource[]) {
		return verfahrenList.find(verfahren => verfahren.aktenzeichen !== this.selectedAktenzeichen);
	}

	private showDialog(additionalContent: string[]) {
		const dialogYesNo = this.dialogService.openDialogYesNo(
			additionalContent.length > 0 ? this.MSG_MULTIPLE : this.MSG_INACTIVATE,
			['Inaktivieren', 'Abbrechen'],
			[],
			additionalContent
		);
		this.subscriptions.push(
			dialogYesNo.pipe(take(1)).subscribe((dialog: DialogDataYesNo) => {
				if (dialog && dialog.agree) {
					const updatedKontakt = { ...this.selectedKontakt, active: false };
					this.facade.updateKontakt(updatedKontakt);
					this.formService.form.reset();

					if (dialog.keyboard) {
						this.focus('kontakt-search-input');
					}
				}
			})
		);
	}

	activate() {
		this.facade.requestActionOnSelectedKontakt();
		const dialogYesNo = this.dialogService.openDialogYesNo(this.MSG_ACTIVATE, ['Aktivieren', 'Abbrechen']);
		this.subscriptions.push(
			dialogYesNo.pipe(take(1)).subscribe((dialog: DialogDataYesNo) => {
				if (dialog && dialog.agree) {
					const updatedKontakt = { ...this.selectedKontakt, active: true };
					this.facade.updateKontakt(updatedKontakt);

					if (dialog.keyboard) {
						this.focus('edit-button');
					}
				}
			})
		);
	}

	private focus(elementId: string) {
		setTimeout(() => {
			this.accessibilityService.focusElementById(elementId);
		});
	}

	inactiveAndSelected(): boolean {
		return !this.selectedKontakt.active && (!this.isPersonendatenEmpty() || !this.isFirmendatenEmpty());
	}

	fillSitzIfNeededAndEmpty(): boolean {
		if (this.editMode) {
			const stadt = this.getFormControl('stadt').value;
			if (!isNil(stadt)) {
				const sitzCtrl = this.getFormControl('sitz');
				if (isNil(sitzCtrl.value)) {
					sitzCtrl.patchValue(stadt);
				}
			}
		}
		return true;
	}

	private matchesInputForm(selected: KontaktResource) {
		const c = this.form.controls;
		for (const key in c) {
			if (key !== 'ssr' && key != 'bsr' && key != 'lr' && c[key].value !== selected[key]) {
				return false;
			}
		}
		return true;
	}
}
